<template>
  <div>
    <div v-if="isLoadingSession && !session" class="tab">
      <div class="tab__header">
        <div class="tab__title h2">Загрузка марафона…</div>
      </div>
    </div>

    <div v-else-if="loadError" class="tab">
      <div class="tab__header">
        <div class="tab__title h2">Марафон не найден</div>
      </div>
      <div class="tab__content">
        <p>Сессия с этим идентификатором не найдена. Возможно, ссылка устарела.</p>
        <router-link :to="{ name: 'marathon' }" class="btn btn-green">К странице марафона</router-link>
      </div>
    </div>

    <template v-else-if="session">
      <div class="tab">
        <div class="tab__header">
          <router-link :to="{ name: 'marathon' }" class="come-back">К списку марафонов</router-link>
          <div class="tab__title h2">Марафон ПДД — {{ session.category_name }}</div>
        </div>

        <div class="marathon-actions">
          <div class="marathon-actions__group">
            <button type="button" class="btn btn-blue marathon-actions__btn" @click="openTrainingModal">
              Ошибки
            </button>
            <router-link
              :to="{ name: 'marathon-themes', params: { uuid: sessionUuid } }"
              class="btn btn-blue marathon-actions__btn"
            >
              Темы с ошибками
            </router-link>
          </div>
          <div class="marathon-actions__group">
            <button type="button" class="btn marathon-actions__btn marathon-actions__btn--subtle" @click="copyLink">
              {{ copyLinkText }}
            </button>
            <button type="button" class="btn marathon-actions__btn marathon-actions__btn--subtle" @click="restartMarathon">
              Начать заново
            </button>
            <button type="button" class="btn marathon-actions__btn marathon-actions__btn--subtle" @click="askDelete">
              Удалить марафон
            </button>
          </div>
        </div>

        <div class="marathon-stats">
          <div class="marathon-stats__row">
            <div class="marathon-stats__row-label marathon-stats__row-label--main">
              <b>{{ progress.mastered }}</b> / {{ progress.total }} выучено
              <span v-if="masteredPercent > 0" class="marathon-stats__pct">({{ masteredPercent }}%)</span>
              / {{ progress.answered }} ответов
              <span
                v-b-tooltip.hover.top="masteredTip"
                class="marathon-stats__info"
                tabindex="0"
              >ⓘ</span>
              <span
                v-b-tooltip.hover.top="phaseTip"
                class="marathon-stats__phase"
              >{{ phaseLabel }}</span>
            </div>
            <div class="marathon-stats__bar">
              <div
                class="marathon-stats__fill marathon-stats__fill--green"
                :style="{ width: masteredPercent + '%' }"
              ></div>
            </div>
          </div>

          <div class="marathon-stats__row">
            <div class="marathon-stats__row-label">
              Охвачено: {{ progress.seenUnique }} / {{ progress.total }}
              <span class="marathon-stats__pct">({{ seenPercent }}%)</span>
              <span
                v-b-tooltip.hover.top="seenTip"
                class="marathon-stats__info"
                tabindex="0"
              >ⓘ</span>
            </div>
            <div class="marathon-stats__bar">
              <div
                class="marathon-stats__fill marathon-stats__fill--blue"
                :style="{ width: seenPercent + '%' }"
              ></div>
            </div>
          </div>

          <div v-if="progress.seenUnique > 0" class="marathon-stats__row">
            <div class="marathon-stats__row-label">
              Результат: {{ correctPercent }}% правильно · {{ wrongPercent }}% неправильно
              <span
                v-b-tooltip.hover.top="ratioTip"
                class="marathon-stats__info"
                tabindex="0"
              >ⓘ</span>
            </div>
            <div class="marathon-stats__bar marathon-stats__bar--ratio">
              <div
                class="marathon-stats__fill marathon-stats__fill--green"
                :style="{ flex: correctPercent }"
              ></div>
              <div
                class="marathon-stats__fill marathon-stats__fill--red"
                :style="{ flex: wrongPercent }"
              ></div>
            </div>
          </div>
        </div>

        <div v-if="isCompleted" class="tab__content marathon-completed">          <div class="h3">Поздравляем! Марафон пройден.</div>
          <p>Вы ответили правильно подряд на все {{ progress.total }} вопросов категории «{{ session.category_name }}».</p>
          <button type="button" class="btn btn-green" @click="restartMarathon">Начать новый марафон</button>
        </div>

        <div v-else-if="currentQuestion" class="tab__question">
          <div v-if="currentQuestion.image" class="ticket-question__image">
            <img :src="`${$APP_URL}/storage/${currentQuestion.image}`" class="mb-3 w-100" />
          </div>

          <div class="ticket-question">
            <div class="ticket-number">Вопрос #{{ currentQuestion.id }}</div>
            <div class="h3" v-html="currentQuestion.text"></div>
          </div>

          <form @submit.prevent>
            <div
              v-for="(answerText, key, index) in currentQuestion.answers"
              :key="key"
              class="checkout-group"
              :class="getAnswerClass(key)"
            >
              <input
                type="radio"
                name="marathon-answer"
                :id="'marathon-answer-' + key"
                class="checkout-group__input"
                :disabled="isAnswered || isLoadingAnswer"
                @click="selectAnswer(key)"
              />
              <label :for="'marathon-answer-' + key" class="checkout-group__label">
                <span>{{ index + 1 }}</span>
                <div class="checkout-group__text">{{ answerText }}</div>

                <div v-if="isAnswered && shouldShowComment(key)" class="answer-info">
                  <hr />
                  <div class="text" v-html="lastAnswerResult.comment || ''"></div>
                </div>
              </label>
            </div>

            <button
              v-if="isAnswered"
              type="button"
              class="btn btn-green"
              :disabled="isLoadingAnswer"
              @click="goToNextQuestion"
            >
              {{ isCompletedAfterAnswer ? 'Завершить' : 'Далее' }}
            </button>

            <div v-if="!isAnswered" class="marathon-skip-row">
              <button
                type="button"
                class="marathon-skip-btn"
                :disabled="isLoadingAnswer"
                @click="handleSkip"
              >
                Простой вопрос
              </button>
              <span
                v-b-tooltip.hover.top="skipTip"
                class="marathon-stats__info"
                tabindex="0"
              >ⓘ</span>
            </div>
          </form>
        </div>

        <div v-else class="tab__content">
          <p>Нет доступных вопросов. Попробуйте обновить страницу.</p>
        </div>
      </div>
    </template>

    <b-modal
      ref="confirm-restart"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
      modal-class="marathon-modal"
    >
      <div class="h3 modal-title">Начать новый марафон?</div>
      <p class="marathon-modal__text">
        Текущий прогресс не пропадёт — он останется доступен по прежней ссылке,
        но из этого окна вы перейдёте к новой сессии с чистого листа.
      </p>
      <a class="btn btn-green modal-btn" @click="confirmRestart">Начать новый</a>
      <a href="#" class="modal-link" @click.prevent="cancelRestart">Отмена</a>
    </b-modal>

    <b-modal
      ref="confirm-delete"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
      modal-class="marathon-modal"
    >
      <div class="h3 modal-title">Удалить марафон?</div>
      <p class="marathon-modal__text">
        Весь прогресс по этому марафону будет удалён безвозвратно — вопросы, ответы, статистика.
        Это действие нельзя отменить.
      </p>
      <a class="btn btn-blue modal-btn" @click="confirmDelete">Удалить</a>
      <a href="#" class="modal-link" @click.prevent="cancelDelete">Отмена</a>
    </b-modal>

    <b-modal
      ref="training-picker"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
      modal-class="marathon-modal"
    >
      <div class="h3 modal-title">Тренировка по ошибкам</div>
      <div class="training-mode-toggle">
        <button
          type="button"
          class="training-mode-toggle__btn"
          :class="{ 'training-mode-toggle__btn--active': trainingMode === 'unmastered' }"
          @click="trainingMode = 'unmastered'"
        >Не исправленные</button>
        <button
          type="button"
          class="training-mode-toggle__btn"
          :class="{ 'training-mode-toggle__btn--active': trainingMode === 'all' }"
          @click="trainingMode = 'all'"
        >Все ошибки</button>
      </div>
      <p class="marathon-modal__text">
        Выберите количество вопросов для тренировки:
      </p>
      <div class="training-picker__options">
        <a class="btn btn-green training-picker__btn" @click="startTrainingWith(10)">10 вопросов</a>
        <a class="btn btn-green training-picker__btn" @click="startTrainingWith(20)">20 вопросов</a>
        <a class="btn btn-green training-picker__btn" @click="startTrainingWith(40)">40 вопросов</a>
        <a class="btn btn-green training-picker__btn" @click="startTrainingWith('all')">Все с ошибками</a>
      </div>
      <a href="#" class="modal-link" @click.prevent="closeTrainingModal">Отмена</a>
    </b-modal>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import ls from '../../services/ls';

  const LS_SESSION_UUID_KEY = 'marathon-session-uuid';

  function buildSessionEntry(session) {
    const s = (session && session.state) || {};
    return {
      uuid: session.uuid,
      category_name: session.category_name || '',
      category_id: session.category_id || null,
      mastered: s.mastered_count || 0,
      total: s.total_questions || 0,
      seen_unique: s.seen_unique || 0,
      correct_last: s.correct_last || 0,
      wrong_last: s.wrong_last || 0,
      total_answered: s.total_answered || 0,
    };
  }

  export default {
    name: 'MarathonSession',
    data() {
      return {
        loadError: false,
        copyLinkText: 'Скопировать ссылку',
        isCompletedAfterAnswer: false,
        pendingNextQuestion: null,
        trainingMode: 'unmastered',
      };
    },
    computed: {
      ...mapGetters('marathon', [
        'session',
        'currentQuestion',
        'lastAnswerResult',
        'isLoadingSession',
        'isLoadingAnswer',
        'isCompleted',
        'progress',
      ]),
      sessionUuid() {
        return this.$route.params.uuid;
      },
      isAnswered() {
        return !!this.lastAnswerResult;
      },
      masteredPercent() {
        if (!this.progress.total) return 0;
        return Math.round((this.progress.mastered / this.progress.total) * 100);
      },
      seenPercent() {
        if (!this.progress.total) return 0;
        return Math.round((this.progress.seenUnique / this.progress.total) * 100);
      },
      correctPercent() {
        const total = this.progress.correctLast + this.progress.wrongLast;
        if (!total) return 0;
        const rounded = Math.round((this.progress.correctLast / total) * 100);
        if (this.progress.wrongLast > 0 && rounded === 100) return 99;
        return rounded;
      },
      wrongPercent() {
        const total = this.progress.correctLast + this.progress.wrongLast;
        if (!total) return 0;
        const rounded = 100 - Math.round((this.progress.correctLast / total) * 100);
        if (this.progress.wrongLast > 0 && rounded === 0) return 1;
        return rounded;
      },
      masteredTip() {
        const n = (this.session && this.session.config && this.session.config.mastery_threshold) || 3;
        return `Вопрос считается выученным, если вы ответили на него правильно ${n} раза подряд — без ошибок.`;
      },
      seenTip() {
        return 'Количество уникальных вопросов, на которые вы ответили хотя бы один раз.';
      },
      ratioTip() {
        return 'Соотношение последних ответов на каждый вопрос среди отвеченных. '
          + 'Если повторно ответить правильно — вопрос сразу становится «зелёным».';
      },
      phaseTip() {
        const { phase } = this.progress;
        if (phase === 'discovery') {
          return 'Знакомство: все вопросы показываются по одному разу в случайном порядке. '
            + 'Этап завершится, когда вы увидите каждый вопрос хотя бы раз.';
        }
        if (phase === 'reinforcement') {
          return 'Закрепление: все вопросы уже показаны. Теперь алгоритм чаще повторяет '
            + 'те, в которых вы ошибались. Этап завершится, когда все вопросы будут выучены.';
        }
        return '';
      },
      skipTip() {
        return 'Нажмите, если уверены, что ответили бы правильно — вопрос будет засчитан как верный ответ. Опция нужна для экономии времени и фокусировки на действительно сложных вопросах.';
      },
      phaseLabel() {
        const { phase } = this.progress;
        if (phase === 'discovery') return 'Этап: знакомство с вопросами';
        if (phase === 'reinforcement') return 'Этап: закрепление';
        if (phase === 'completed') return 'Завершён';
        return '';
      },
    },
    methods: {
      ...mapActions('marathon', ['loadSession', 'submitAnswer', 'skipQuestion', 'createSession', 'advanceToNextQuestion', 'clearSession', 'deleteSession']),
      async fetchSession() {
        this.loadError = false;
        try {
          const session = await this.loadSession({ uuid: this.sessionUuid });
          ls.set(LS_SESSION_UUID_KEY, this.sessionUuid);
          if (session) {
            ls.upsertMarathonSession(buildSessionEntry(session));
          }
        } catch (e) {
          this.loadError = true;
          const status = e && e.response && e.response.status;
          if (status === 404) {
            ls.removeMarathonSession(this.sessionUuid);
          }
        }
      },
      getAnswerClass(key) {
        if (!this.isAnswered) return {};

        const isCorrect = key === this.lastAnswerResult.correct_answer;
        const isUserAnswer = key === this.lastAnswerResult.answer;

        return {
          'answer-right': isCorrect,
          'answer-wrong': isUserAnswer && !isCorrect,
        };
      },
      shouldShowComment(key) {
        if (!this.isAnswered) return false;
        return key === this.lastAnswerResult.correct_answer
          && this.lastAnswerResult.answer !== this.lastAnswerResult.correct_answer;
      },
      async selectAnswer(answerKey) {
        if (this.isAnswered || this.isLoadingAnswer) return;

        try {
          const resp = await this.submitAnswer({
            uuid: this.sessionUuid,
            questionId: this.currentQuestion.id,
            answer: answerKey,
          });

          this.isCompletedAfterAnswer = !!(resp && resp.completed);
          this.pendingNextQuestion = (resp && resp.next_question) || null;

          if (resp && resp.is_correct && !resp.completed) {
            setTimeout(() => {
              if (this.isAnswered) {
                this.goToNextQuestion();
              }
            }, 700);
          }
        } catch (e) {
          // отображение ошибки делегируем глобально (errors в store)
        }
      },
      async handleSkip() {
        if (this.isAnswered || this.isLoadingAnswer) return;
        try {
          const resp = await this.skipQuestion({
            uuid: this.sessionUuid,
            questionId: this.currentQuestion.id,
          });
          this.advanceToNextQuestion((resp && resp.next_question) || null);
          this.isCompletedAfterAnswer = false;
          if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (e) {
          // ошибки обрабатываются глобально
        }
      },
      goToNextQuestion() {
        const next = this.pendingNextQuestion;
        this.pendingNextQuestion = null;
        this.advanceToNextQuestion(next);
        this.isCompletedAfterAnswer = false;

        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      copyLink() {
        const url = window.location.href;
        const onSuccess = () => {
          this.copyLinkText = 'Ссылка скопирована';
          setTimeout(() => { this.copyLinkText = 'Скопировать ссылку'; }, 2000);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(onSuccess).catch(() => {
            this.fallbackCopy(url, onSuccess);
          });
        } else {
          this.fallbackCopy(url, onSuccess);
        }
      },
      fallbackCopy(text, onSuccess) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
          onSuccess();
        } catch (e) {
          // noop
        }
        document.body.removeChild(ta);
      },
      openTrainingModal() {
        this.$refs['training-picker'].show();
      },
      closeTrainingModal() {
        this.$refs['training-picker'].hide();
      },
      startTrainingWith(n) {
        this.$refs['training-picker'].hide();
        this.$router.push({
          name: 'marathon-training',
          params: { uuid: this.sessionUuid },
          query: { n, mode: this.trainingMode },
        });
      },
      restartMarathon() {
        this.$refs['confirm-restart'].show();
      },
      cancelRestart() {
        this.$refs['confirm-restart'].hide();
      },
      async confirmRestart() {
        this.$refs['confirm-restart'].hide();
        try {
          const newSession = await this.createSession({ category_id: this.session.category_id });
          if (newSession && newSession.uuid) {
            ls.set(LS_SESSION_UUID_KEY, newSession.uuid);
            ls.upsertMarathonSession(buildSessionEntry(newSession));
            this.$router.push({ name: 'marathon-session', params: { uuid: newSession.uuid } });
          }
        } catch (e) {
          // ошибка отобразится через store.errors
        }
      },
      askDelete() {
        this.$refs['confirm-delete'].show();
      },
      cancelDelete() {
        this.$refs['confirm-delete'].hide();
      },
      async confirmDelete() {
        this.$refs['confirm-delete'].hide();
        try {
          await this.deleteSession({ uuid: this.sessionUuid });
          ls.removeMarathonSession(this.sessionUuid);
          this.$router.push({ name: 'marathon' });
        } catch (e) {
          // ошибка отобразится через store.errors
        }
      },
    },
    watch: {
      sessionUuid: {
        immediate: true,
        handler() {
          if (this.sessionUuid) {
            this.fetchSession();
          }
        },
      },
    },
    beforeDestroy() {
      this.clearSession();
    },
  };
</script>

<style scoped>
.marathon-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 8px;
  padding-right: 26px;
  @media screen and (max-width: 992px) {
    padding-right: 15px;
  }
}
.marathon-actions__group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.marathon-actions__btn {
  width: auto;
  min-width: 0;
  height: 36px;
  line-height: 36px;
  padding: 0 16px;
  margin: 0;
  font-size: 13px;
}
.marathon-actions__btn--subtle {
  background: #f2f2f2;
  color: #444;
  border: 1px solid #e0e0e0;
  font-weight: normal;
  &:hover {
    background: #e8e8e8;
    border-color: #d0d0d0;
    color: #222;
  }
}
.marathon-stats {
  margin: 16px 0 52px;
  padding-right: 26px;
  @media screen and (max-width: 992px) {
    padding-right: 15px;
  }
}
.marathon-stats__pct {
  color: #aaa;
  font-size: 12px;
}
.marathon-stats__phase {
  margin-left: auto;
  font-size: 12px;
  color: #999;
  border-bottom: 1px dashed #bbb;
  cursor: default;
  flex-shrink: 0;
}
.marathon-stats__row {
  margin-bottom: 8px;
}
.marathon-stats__row-label {
  font-size: 12px;
  color: #555;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 4px;
  &--main {
    font-size: 15px;
    color: #333;
  }
}
.marathon-stats__info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 11px;
  font-style: normal;
  color: #999;
  border: 1px solid #ccc;
  border-radius: 50%;
  cursor: default;
  line-height: 1;
  flex-shrink: 0;
  &:hover {
    color: #555;
    border-color: #888;
  }
}
.marathon-stats__bar {
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}
.marathon-stats__bar--ratio {
  display: flex;
  background: transparent;
}
.marathon-stats__fill {
  height: 100%;
  transition: width 0.3s ease, flex 0.3s ease;
}
.marathon-stats__fill--blue {
  background: #2196f3;
}
.marathon-stats__fill--green {
  background: #4caf50;
}
.marathon-stats__fill--red {
  background: #e53935;
}
.marathon-skip-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
}
.marathon-skip-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: #999;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  line-height: 1;
  &:hover:not(:disabled) {
    color: #555;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}
.marathon-completed {
  text-align: center;
  padding: 32px 16px;
}
.marathon-completed .btn {
  margin-top: 16px;
}
</style>

<style>
.marathon-modal .modal-title {
  text-align: center;
  margin-bottom: 16px;
}
.marathon-modal__text {
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: #555;
  margin-bottom: 24px;
}
.marathon-modal .modal-btn {
  margin: 0 auto 12px;
  display: block;
}
.marathon-modal .modal-link {
  display: block;
  text-align: center;
  font-size: 13px;
  color: #888;
  text-decoration: underline;
}
.training-picker__options {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.training-picker__btn {
  width: auto;
  min-width: 200px;
  margin: 0;
  cursor: pointer;
}
.training-mode-toggle {
  display: flex;
  background: #f0f0f0;
  border-radius: 20px;
  padding: 3px;
  margin: 0 auto 20px;
  width: fit-content;
}
.training-mode-toggle__btn {
  background: none;
  border: none;
  padding: 6px 16px;
  border-radius: 17px;
  font-size: 14px;
  color: #888;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  line-height: 1.4;
}
.training-mode-toggle__btn--active {
  background: #fff;
  color: #333;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
</style>
